import { useEffect, useRef } from 'react'

import { useSelector } from 'react-redux'

import { env } from '@/core/environments'
import { RootState } from '@/core/store'
import { identifyAmplitude, resetAmplitude } from '@/shared/analytics/amplitude'

/**
 * Identifies the authenticated user in Amplitude, mirroring the same
 * activation logic and identity fields used by PostHogIdentify.
 *
 * Activation levels:
 *   'all'     — identify every authenticated user
 *   'partial' — identify only when user.account.test is true (staging/dev validation)
 *   'none'    — disabled (e.g. localhost)
 */
export const AmplitudeIdentify = () => {
	const { user, isAuthenticated } = useSelector(
		(state: RootState) => state.auth
	)
	const lastIdentifiedId = useRef<string | null>(null)

	useEffect(() => {
		if (env.ENABLE_AMPLITUDE === 'none') {
			return
		}

		if (isAuthenticated && user) {
			const shouldIdentify =
				env.ENABLE_AMPLITUDE === 'all' ||
				(env.ENABLE_AMPLITUDE === 'partial' && user.account?.test)

			if (!shouldIdentify) {
				return
			}

			// Amplitude requires user_id to be at least 5 characters.
			// Prefix the numeric ID to guarantee that constraint is always met.
			const userId = `usr_${user.id}`

			if (lastIdentifiedId.current === userId) {
				return
			}

			lastIdentifiedId.current = userId

			identifyAmplitude(userId, {
				email: user.email,
				user_name: user.getFullName(),
				user_role: user.role,
				account_id: user.accountId,
				account_name: user.account?.name,
				account_country: user.account?.country,
				account_mrr: user.account?.metadata?.total_mrr,
				account_segmentation: user.account?.metadata?.segmentation,
				account_players_count: user.account?.playersCount,
				account_boxes_count: user.account?.availableBoxes,
			})
		} else if (!isAuthenticated && !user && lastIdentifiedId.current) {
			resetAmplitude()
			lastIdentifiedId.current = null
		}
	}, [isAuthenticated, user])

	return null
}
